// 인증 유틸리티 함수

// 토큰 저장
export const setToken = (token) => {
  localStorage.setItem('trading_token', token);
};

// 토큰 가져오기
export const getToken = () => {
  return localStorage.getItem('trading_token');
};

// 토큰 삭제
export const removeToken = () => {
  localStorage.removeItem('trading_token');
};

// 로그아웃
export const logout = () => {
  removeToken();
  // 페이지 새로고침 또는 리다이렉트
  window.location.reload();
};

// 전역 Rate Limiting 관리
const RATE_LIMIT_STORAGE_KEY = 'global_rate_limit_until';

// Rate limiting 상태 확인
export const isRateLimited = () => {
  const rateLimitUntil = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  if (!rateLimitUntil) return false;
  
  const now = Date.now();
  const until = parseInt(rateLimitUntil, 10);
  
  if (now >= until) {
    // 시간이 지났으면 상태 제거
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    return false;
  }
  
  return true;
};

// Rate limiting 남은 시간 가져오기 (밀리초)
export const getRateLimitRemaining = () => {
  const rateLimitUntil = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  if (!rateLimitUntil) return 0;
  
  const now = Date.now();
  const until = parseInt(rateLimitUntil, 10);
  
  if (now >= until) {
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    return 0;
  }
  
  return until - now;
};

// Rate limiting 설정
export const setRateLimited = (waitTimeMs) => {
  const until = Date.now() + waitTimeMs;
  localStorage.setItem(RATE_LIMIT_STORAGE_KEY, until.toString());
  console.log(`🚫 전역 Rate Limiting 활성화: ${Math.ceil(waitTimeMs / 1000)}초 후 해제`);
};

// Rate limiting 해제
export const clearRateLimit = () => {
  localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
  console.log('✅ 전역 Rate Limiting 해제');
};

// 사용자 정보 조회 캐싱 및 중복 요청 방지
const USER_INFO_CACHE_KEY = 'user_info_cache';
const USER_INFO_CACHE_TTL = 30 * 1000; // 30초 캐시
let pendingUserInfoRequest = null;

// 사용자 정보 캐시 가져오기
const getUserInfoCache = () => {
  try {
    const cached = localStorage.getItem(USER_INFO_CACHE_KEY);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // 캐시가 만료되었으면 제거
    if (now - timestamp > USER_INFO_CACHE_TTL) {
      localStorage.removeItem(USER_INFO_CACHE_KEY);
      return null;
    }
    
    return data;
  } catch (e) {
    return null;
  }
};

// 사용자 정보 캐시 저장
const setUserInfoCache = (userData) => {
  try {
    const cacheData = {
      data: userData,
      timestamp: Date.now()
    };
    localStorage.setItem(USER_INFO_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    // localStorage 저장 실패 시 무시
  }
};

// 사용자 정보 캐시 삭제
export const clearUserInfoCache = () => {
  localStorage.removeItem(USER_INFO_CACHE_KEY);
};

// 사용자 정보 조회 (캐싱 및 중복 요청 방지)
export const fetchUserInfo = async (forceRefresh = false) => {
  // 강제 새로고침이면 캐시 삭제 후 새로 조회 (적용/해제 후 리스트 반영용)
  if (forceRefresh) {
    clearUserInfoCache();
    pendingUserInfoRequest = null;
  } else {
    const cached = getUserInfoCache();
    if (cached) {
      console.log('✅ 사용자 정보 캐시 사용');
      return cached;
    }
  }

  // 강제 새로고침이면 진행 중인 요청 재사용하지 않고 새로 조회
  if (!forceRefresh && pendingUserInfoRequest) {
    console.log('⏳ 진행 중인 사용자 정보 조회 대기');
    return pendingUserInfoRequest;
  }

  // 새 요청 시작
  pendingUserInfoRequest = (async () => {
    try {
      const token = getToken();
      if (!token) {
        clearUserInfoCache();
        return null;
      }
      
      // 타임아웃 설정 (60초) - Trading-service 응답이 느릴 수 있음
      const response = await authenticatedFetch('/api/user/me', {
        timeout: 60000
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          console.warn('⚠️ 사용자 정보 조회 응답이 HTML입니다. API 라우팅을 확인해주세요.');
          return null;
        }
        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error('❌ 사용자 정보 JSON 파싱 실패:', parseError);
          return null;
        }
        const userData = data.user || data;
        
        // 캐시 저장
        setUserInfoCache(userData);
        
        return userData;
      } else if (response.status === 401) {
        // 인증 실패 시 캐시 삭제
        clearUserInfoCache();
        removeToken();
        return null;
      } else if (response.status === 429) {
        // Rate limit 에러는 캐시된 데이터가 있으면 반환
        const cached = getUserInfoCache();
        if (cached) {
          console.log('⚠️ Rate limit 발생, 캐시된 사용자 정보 사용');
          return cached;
        }
        throw new Error('Rate limit exceeded');
      } else {
        // 기타 에러는 캐시된 데이터가 있으면 반환
        const cached = getUserInfoCache();
        if (cached) {
          console.log('⚠️ 사용자 정보 조회 실패, 캐시된 데이터 사용');
          return cached;
        }
        return null;
      }
    } catch (error) {
      // 타임아웃 에러 처리
      if (error.name === 'TimeoutError' || error.message?.includes('시간이 초과')) {
        const cached = getUserInfoCache();
        if (cached) {
          console.log('⚠️ 사용자 정보 조회 타임아웃, 캐시된 데이터 사용');
          return cached;
        }
        console.error('❌ 사용자 정보 조회 타임아웃 및 캐시 없음:', error.message);
      }
      
      // 에러 발생 시 캐시된 데이터가 있으면 반환
      const cached = getUserInfoCache();
      if (cached) {
        console.log('⚠️ 사용자 정보 조회 에러, 캐시된 데이터 사용:', error.message);
        return cached;
      }
      throw error;
    } finally {
      // 요청 완료 후 pending 요청 초기화
      pendingUserInfoRequest = null;
    }
  })();
  
  return pendingUserInfoRequest;
};

// 인증된 fetch 요청
export const authenticatedFetch = async (url, options = {}) => {
  // 전역 Rate Limiting 체크
  if (isRateLimited()) {
    const remaining = getRateLimitRemaining();
    const remainingSeconds = Math.ceil(remaining / 1000);
    const remainingMinutes = Math.floor(remainingSeconds / 60);
    const remainingSecs = remainingSeconds % 60;
    
    console.warn(`⏸️ 전역 Rate Limiting 활성화 - 요청 차단: ${remainingMinutes}분 ${remainingSecs}초 후 재시도 가능`, {
      url,
      method: options.method || 'GET'
    });
    
    // 429 에러를 시뮬레이션하여 호출자에게 알림
    const error = new Error('Rate limit exceeded');
    error.status = 429;
    error.retryAfter = remainingSeconds;
    throw error;
  }

  const token = getToken();
  const skipAuthHandling = options.skipAuthHandling === true;
  
  const apiBaseFromEnv = process.env.REACT_APP_TRADING_API_BASE;
  const derivedApiBase = (() => {
    if (apiBaseFromEnv) {
      return apiBaseFromEnv;
    }
    const host = window.location.hostname;
    // star612.net에서는 동일 오리진(/api) 프록시를 사용
    if (host === 'star612.net' || host === 'www.star612.net') {
      return window.location.origin;
    }
    // trading.star612.net 접속 시에도 동일 오리진 사용
    if (host === 'trading.star612.net') {
      return window.location.origin;
    }
    return window.location.origin;
  })();

  // 상대 경로를 절대 경로로 변환
  // star612.net(또는 www)에서 /api/* 호출 시 항상 /trading/api/* 로 보내 프록시를 타도록 함
  const host = window.location.hostname;
  // star612.net /stockai 에서도 trading API 프록시 사용
  const useTradingProxy =
    host === 'star612.net' ||
    host === 'www.star612.net' ||
    window.location.pathname.startsWith('/trading') ||
    window.location.pathname.startsWith('/stockai');
  const absoluteUrl = url.startsWith('http')
    ? url
    : url.startsWith('/api/')
      ? (useTradingProxy
          ? `${window.location.origin}/trading${url}` // /trading/api/... → star612 서버 프록시 → trading-service
          : `${derivedApiBase}${url}`)
      : url.startsWith('/')
        ? `${window.location.origin}${url}`
        : `${window.location.origin}/${url}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  console.log('🔐 Authenticated fetch:', {
    url: absoluteUrl,
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    method: options.method || 'GET'
  });

  // 타임아웃 설정 (기본 30초, 옵션으로 오버라이드 가능)
  const timeout = options.timeout || 30000; // 기본 30초
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  // 요청 실행 (429 에러는 재시도하지 않고 즉시 처리)
  let response;
  try {
    response = await fetch(absoluteUrl, {
      ...options,
      headers,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);

    // 429 에러 발생 시 즉시 처리 (재시도하지 않음)
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitTimeMs = retryAfter ? parseInt(retryAfter) * 1000 : 15 * 60 * 1000; // 기본 15분
      
      // 전역 rate limit 설정
      setRateLimited(waitTimeMs);
      
      const waitMinutes = Math.ceil(waitTimeMs / 60000);
      const waitSeconds = Math.ceil(waitTimeMs / 1000);
      console.warn(`🚫 Rate Limiting (429): 전역 rate limit 설정 - ${waitMinutes}분 (${waitSeconds}초) 후 재시도 가능`);
      
      // 429 에러를 그대로 반환 (호출자가 처리하도록)
      return response;
    }
  } catch (fetchError) {
    clearTimeout(timeoutId);
    
    // 타임아웃 에러 처리
    if (fetchError.name === 'AbortError') {
      const timeoutError = new Error(`요청 시간이 초과되었습니다 (${timeout}ms)`);
      timeoutError.name = 'TimeoutError';
      timeoutError.status = 504;
      throw timeoutError;
    }
    
    // 네트워크 에러는 그대로 전달
    throw fetchError;
  }

  // Content-Type 확인 (HTML 응답 방지) - response를 clone하지 않고 바로 확인
  let contentType = null;
  let isHTMLResponse = false;
  try {
    contentType = response.headers.get('content-type');
    isHTMLResponse = contentType && contentType.includes('text/html');
  } catch (e) {
    // 헤더 읽기 실패 시 무시
  }

  // 502 Bad Gateway 에러 처리
  if (response.status === 502) {
    console.error('❌ 502 Bad Gateway:', {
      url: absoluteUrl,
      status: response.status,
      statusText: response.statusText,
      message: 'Trading-service가 응답하지 않습니다. 서버 상태를 확인해주세요.'
    });
    
    // HTML 응답을 JSON으로 파싱하려고 하지 않도록 처리
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('text/html')) {
      const error = new Error('서버 연결 오류가 발생했습니다. Trading-service가 실행 중인지 확인해주세요.');
      error.status = 502;
      error.isServerError = true;
      throw error;
    }
    
    const error = new Error('서버 연결 오류가 발생했습니다. Trading-service가 실행 중인지 확인해주세요.');
    error.status = 502;
    error.isServerError = true;
    throw error;
  }

  // 503 Service Unavailable 에러 처리
  if (response.status === 503) {
    console.error('❌ 503 Service Unavailable:', {
      url: absoluteUrl,
      status: response.status,
      statusText: response.statusText
    });
    throw new Error('서비스가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
  }

  // 500 Internal Server Error 처리
  if (response.status === 500) {
    console.error('❌ 500 Internal Server Error:', {
      url: absoluteUrl,
      status: response.status,
      statusText: response.statusText
    });
    // 500 에러는 기존 로직대로 처리 (JSON 파싱 시도)
  }

  // HTML 응답인 경우 (에러 페이지 등) 조용히 처리
  if (isHTMLResponse && response.status !== 200) {
    // HTML 응답은 JSON 파싱 시도하지 않도록 처리
    // 에러는 호출하는 쪽에서 처리하도록 함
    console.log('⚠️ HTML response received (may indicate routing issue):', {
      url: absoluteUrl,
      status: response.status
    });
  }

  // 401 에러 시 토큰 삭제 및 로그인 페이지로 리다이렉트
  if (response.status === 401) {
    if (skipAuthHandling) {
      return response;
    }
    // HTML 응답이 아닌 경우에만 로그 출력
    if (!isHTMLResponse) {
      console.error('❌ 401 Unauthorized:', {
        url: absoluteUrl,
        status: response.status,
        statusText: response.statusText,
        hasToken: !!token,
        tokenLength: token ? token.length : 0
      });
    }
    
    // 토큰이 있을 때만 삭제 (미로그인 401은 정상)
    if (token) {
      removeToken();
    }
    
    // 에러 응답 본문 확인 (HTML 응답 방지)
    if (!isHTMLResponse) {
      try {
        const errorData = await response.clone().json();
        console.error('401 Error details:', errorData);
      } catch (e) {
        // JSON 파싱 실패 시 무시
      }
    }
    
    throw new Error('Authentication required');
  }

  // 404 에러 처리 (HTML 응답 방지)
  if (response.status === 404) {
    if (isHTMLResponse) {
      // HTML 응답인 경우 조용히 처리 (nginx 프록시 설정 문제일 수 있음)
      console.log('⚠️ 404 Not Found (HTML response - may indicate routing issue):', {
        url: absoluteUrl
      });
    }
    // 404 에러는 호출하는 쪽에서 처리하도록 함 (조용히 처리)
  }

  return response;
};

